import type { CityConfig } from "@/pages/city-template/types";

export const queenCreekConfig: CityConfig = {
  city: "Queen Creek",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "queen-creek",
  route: "/queen-creek",
  title: "Emergency & Expedited Passport Services Queen Creek AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Queen Creek, AZ — as fast as 24 hours. No Queen Creek passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 105 miles south. A+ BBB rated. Serving San Tan Valley, young families, and Southeast Valley travelers.",
  h1: "Emergency & Expedited Passport Services in Queen Creek, AZ",
  heroSubtitle: "Queen Creek sits in the rapidly growing Southeast Valley, about 105 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a young family planning a first cruise, a San Tan Valley commuter heading abroad for business, or a Queen Creek adventurer with international travel on the horizon.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Queen%20Creek%20Arizona%20scenic%20view%20of%20the%20San%20Tan%20Mountains%20at%20golden%20hour%20with%20sprawling%20master-planned%20community%20neighborhoods%20in%20the%20foreground%2C%20dramatic%20desert%20landscape%20with%20saguaro%20silhouettes%2C%20modern%20suburban%20homes%20with%20terracotta%20roofs%2C%20professional%20wide%20angle%20landscape%20photography%2C%20warm%20amber%20sunset%20lighting%2C%20no%20people%20visible&width=1800&height=700&seq=qc-hero-001&orientation=landscape",
  heroImageAlt: "Queen Creek Arizona with San Tan Mountains at golden hour sunset",
  geoPosition: "33.2487;-111.6343",
  geoPlacename: "Queen Creek, Arizona",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 105 miles from Queen Creek — about 1 hour 45 minutes via Hunt Highway and I-10",
  },
  nearbyCitiesZips: [
    { name: "Queen Creek", zips: "85142, 85140" },
    { name: "San Tan Valley", zips: "85140, 85143" },
    { name: "Florence", zips: "85132" },
    { name: "Casa Grande", zips: "85122, 85194" },
    { name: "Coolidge", zips: "85128" },
    { name: "Eloy", zips: "85131" },
    { name: "Maricopa", zips: "85138" },
    { name: "Chandler", zips: "85225, 85286" },
    { name: "Gilbert", zips: "85234, 85295" },
    { name: "Apache Junction", zips: "85119, 85120" },
    { name: "Gold Canyon", zips: "85118" },
    { name: "Sacaton", zips: "85147" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 105 miles from Queen Creek" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Southeast Valley Hub", desc: "Serving Queen Creek, San Tan Valley, Florence, and all Pinal County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Queen Creek case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Chandler AZ", route: "/chandler" },
    { name: "Expedited passport in Gilbert AZ", route: "/gilbert" },
    { name: "Urgent passport in Mesa AZ", route: "/mesa" },
    { name: "Arizona passport hub", route: "/arizona" },
    { name: "Fast passport in Casa Grande AZ", route: "/casa-grande" },
    { name: "Emergency passport in Phoenix AZ", route: "/phoenix" },
    { name: "Expedited passport in Tucson AZ", route: "/tucson" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "Fast Passport Center serves Queen Creek remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. Queen Creek's position in the Southeast Valley along the Hunt Highway makes overnight document shipping fast and reliable to the Tucson passport agency.",
  fpcOfficeDistance: "Serving all of Pinal County and the Southeast Valley",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Queen Creek, San Tan Valley, Florence, and the Pinal County corridor. From growing families planning first cruises to business travelers commuting from the Southeast Valley, we help Queen Creek residents get their passports fast.",
  nearbyCitiesIntro: "We serve Queen Creek and nearby communities including San Tan Valley, Florence, Casa Grande, Coolidge, Eloy, and the entire Southeast Valley area — from Gilbert to Maricopa.",
  howItWorksIntro: "Most Queen Creek travelers complete everything online and ship documents overnight. At about 105 miles from Tucson, Fast Passport Center provides fast courier turnaround for Southeast Valley residents — ideal for San Tan Valley families and Queen Creek professionals planning international trips.",
  mistakesIntro: "Queen Creek travelers, including young families planning first cruises and San Tan Valley commuters heading abroad for work, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Queen Creek and Southeast Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Queen Creek? Here are the most common ones we hear from Pinal County travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 105 miles to Tucson (under 2-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for Queen Creek families planning first cruises",
        "Perfect for San Tan Valley professionals traveling abroad",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from PHX or AZA",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Queen Creek is about 105 miles from the Western Passport Center in Tucson. Our couriers make same-day delivery possible, as fast as 24 hours.",
      bullets: ["Southeast Valley coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Queen Creek adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Queen Creek and San Tan Valley"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Queen Creek and Pinal County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Queen Creek, San Tan Valley, and Pinal County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Pinal County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Queen Creek and Southeast Valley residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Queen Creek AZ", description: "Urgent passport processing for Queen Creek, AZ travelers via Western Passport Center courier — about 105 miles from Queen Creek." },
    { name: "Passport Renewal Queen Creek AZ", description: "Fast passport renewal for Queen Creek and Pinal County, Arizona residents." },
    { name: "New Passport Queen Creek AZ", description: "First-time passport application assistance for Queen Creek and Southeast Valley area residents." },
    { name: "Lost Passport Queen Creek AZ", description: "Passport replacement for lost or stolen passports for Queen Creek and Pinal County residents." },
    { name: "Child Passport Queen Creek AZ", description: "Minor passport services for Queen Creek and Pinal County families." },
    { name: "Name Change Passport Queen Creek AZ", description: "Passport name change service for Queen Creek, AZ residents." },
  ],
  testimonials: [
    {
      name: "Jessica & Ryan L.",
      location: "Queen Creek, AZ",
      rating: 5,
      text: "We're a young family of five planning our first Disney cruise to the Bahamas. Realized none of us had passports with 3 weeks to go. Fast Passport Center handled all five applications — from first-timer DS-11 for the kids to renewals for us. All passports arrived in 7 business days.",
      service: "Family Passports",
    },
    {
      name: "Mark T.",
      location: "San Tan Valley, AZ",
      rating: 5,
      text: "My company sent me to Singapore for a manufacturing site visit with 5 days notice. Expired passport. Fast Passport Center got me an emergency renewal in 48 hours. Made the flight. Lifesavers for Southeast Valley professionals.",
      service: "Emergency Passport",
    },
    {
      name: "Amanda G.",
      location: "Queen Creek, AZ",
      rating: 5,
      text: "Destination wedding in Cabo — realized my passport was set to expire 3 months after travel. Mexico requires 6 months validity. Fast Passport Center walk me through the DS-82 renewal and got my passport back in 5 days.",
      service: "Expedited Renewal",
    },
    {
      name: "The Martinez Family",
      location: "Florence, AZ",
      rating: 5,
      text: "Pinal County family with relatives in Hermosillo. Three kids needed first passports for a quinceañera in Mexico. Fast Passport Center made the whole process painless — acceptance clerk appointment at the Queen Creek post office, then rushed everything. Four weeks became 6 business days.",
      service: "Family Passports",
    },
  ],
  facilities: [
    {
      id: "qc-1",
      name: "Queen Creek Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "22424 S Ellsworth Loop Rd",
      city: "Queen Creek",
      state: "AZ",
      zip: "85142",
      phone: "(480) 987-9478",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Queen Creek",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Queen Creek and San Tan Valley residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "qc-2",
      name: "San Tan Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "45 E Combs Rd",
      city: "San Tan Valley",
      state: "AZ",
      zip: "85140",
      phone: "(480) 987-1569",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "San Tan Valley — 5 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for San Tan Valley and southern Queen Creek residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "qc-3",
      name: "Florence Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 N Main St",
      city: "Florence",
      state: "AZ",
      zip: "85132",
      phone: "(520) 868-2221",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles southeast in Florence",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Florence and eastern Pinal County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "qc-4",
      name: "Gilbert Post Office — Pecos Station",
      type: "USPS Passport Acceptance Facility",
      address: "3356 E Pecos Rd",
      city: "Gilbert",
      state: "AZ",
      zip: "85295",
      phone: "(480) 988-9800",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "10 miles north in Gilbert",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Queen Creek and San Tan Valley residents.",
      icon: "ri-mail-line",
    },
    {
      id: "qc-5",
      name: "Casa Grande Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1675 N Pinal Ave",
      city: "Casa Grande",
      state: "AZ",
      zip: "85122",
      phone: "(520) 836-4715",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "30 miles south in Casa Grande",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Pinal County and western Queen Creek residents.",
      icon: "ri-mail-line",
    },
    {
      id: "qc-6",
      name: "Pinal County Clerk of Superior Court — Florence",
      type: "County Clerk Passport Acceptance",
      address: "971 N Jason Lopez Circle, Bldg A",
      city: "Florence",
      state: "AZ",
      zip: "85132",
      phone: "(520) 866-5300",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "15 miles southeast in Florence",
      website: "https://www.pinalcourtsaz.gov/clerk",
      websiteLabel: "Pinal County Clerk",
      notes: "Accepts passport applications for Pinal County residents. County seat location in Florence.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "105 miles — under 2-hr drive, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Driving to Tucson Without an Appointment",
      description: "Queen Creek is only 105 miles from Tucson — but the Western Passport Center requires pre-scheduled appointments. You can't just drive down Hunt Highway and walk in. A wasted 4-hour round trip is not how you want to spend your day.",
    },
    {
      icon: "ri-image-line",
      title: "Queen Creek Drugstore Photo Rejections",
      description: "While CVS and Walgreens in Queen Creek and Gilbert offer passport photos, not all technicians are trained correctly. Passport photos must be exactly 2x2 inches on a white background with proper head positioning. Always verify before leaving the store.",
    },
    {
      icon: "ri-file-list-line",
      title: "First-Time Passport Confusion — Young Families",
      description: "Queen Creek's young families applying for children's first passports often bring the wrong form. Minors under 16 require Form DS-11 with both parents present. Form DS-82 is only for adult renewals — even parents using DS-82 for themselves need DS-11 for kids.",
    },
    {
      icon: "ri-parent-line",
      title: "Both Parents Not Available for Minors",
      description: "Queen Creek's growing families often have one parent commuting to Phoenix for work. Both parents must appear in person for a minor's DS-11 — or the absent parent must provide notarized Form DS-3053. This requirement catches many Queen Creek families off guard.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Summer Application Crush in the Southeast Valley",
      description: "Queen Creek's population of young families means summer is peak passport season. Standard USPS processing can stretch to 13+ weeks from June to August. Start passport applications by April for summer travel — especially for cruise and Mexico vacations.",
    },
    {
      icon: "ri-key-line",
      title: "Confusing Passport Cards with Passport Books for Cruises",
      description: "Many Queen Creek families book cruises and assume a passport card is sufficient. Passport cards are only valid for land and sea border crossings from Canada, Mexico, and the Caribbean. Cruise re-entry into the U.S. requires a full passport book.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Queen Creek, Gilbert, or Chandler." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — about 105 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Queen Creek or San Tan Valley address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Queen Creek from a passport agency?",
      a: "Queen Creek is approximately 105 miles from the Western Passport Center in Tucson — about 1 hour 45 minutes via Hunt Highway and I-10. However, the Tucson agency requires pre-scheduled appointments and does not accept walk-ins. Fast Passport Center handles the courier delivery so you don't need to make the trip.",
    },
    {
      q: "Does Queen Creek have its own passport office?",
      a: "Queen Creek has a USPS passport acceptance facility at 22424 S Ellsworth Loop Road where you can submit first-time applications in person. For expedited processing beyond standard USPS timelines, Fast Passport Center's courier service achieves 2–9 business day turnaround via the Western Passport Center in Tucson.",
    },
    {
      q: "How fast can I get an emergency passport in Queen Creek?",
      a: "Emergency processing is typically 24–48 hours for Queen Creek residents. Our couriers deliver directly to the Western Passport Center in Tucson, about 105 miles away. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "We're a Queen Creek family of five — can you handle all our passports?",
      a: "Absolutely. Queen Creek's young families are some of our most frequent clients. We handle family groups — from first-time child passports (DS-11 with both parents) to adult renewals (DS-82). You only need one acceptance clerk appointment at the Queen Creek Post Office for the first-time applicants.",
    },
    {
      q: "Where do Queen Creek travelers fly internationally?",
      a: "Queen Creek travelers typically fly internationally through Phoenix Sky Harbor International Airport (PHX, about 40 miles northwest) or Phoenix-Mesa Gateway Airport (AZA, about 15 miles north) for domestic connections to international hubs. A valid U.S. passport is required for all international flights.",
    },
    {
      q: "How do I renew my passport in Queen Creek?",
      a: "Adult renewals (16 or older, issued within 15 years, same name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "Where can I get passport photos in Queen Creek?",
      a: "Walgreens at Ellsworth and Hunt Highway, CVS at Ellsworth and Ocotillo, and the UPS Store on Rittenhouse Road all offer passport photo services in Queen Creek. Always verify your photos meet the exact 2x2 inch specifications with a white background.",
    },
    {
      q: "Do you serve San Tan Valley and Florence residents too?",
      a: "Yes. We serve all of Pinal County including San Tan Valley, Florence, Coolidge, Eloy, and the entire Southeast Valley corridor. Documents ship overnight from any UPS location in the Queen Creek and Gilbert area.",
    },
    {
      q: "Can I use a passport card for my cruise from California?",
      a: "No. Passport cards are only valid for land border crossings and sea travel within the Western Hemisphere. For cruises that re-enter the U.S. by air or cruise ship, you need a full passport book. Queen Creek cruise travelers should apply for a passport book well before their sail date.",
    },
    {
      q: "How does Queen Creek's rapid growth affect passport demand?",
      a: "Queen Creek is one of Arizona's fastest-growing communities, which means local acceptance facilities can get booked weeks in advance — especially during summer. Schedule your acceptance clerk appointment early and use Fast Passport Center's courier service for processing speed.",
    },
    {
      q: "What documents do I need for a child's first passport in Queen Creek?",
      a: "For minors under 16: Form DS-11, certified birth certificate, both parents' photo IDs, two passport photos, and fee payment. Both parents must appear in person — or one parent with notarized Form DS-3053. The Queen Creek Post Office on Ellsworth Loop is the closest acceptance facility.",
    },
    {
      q: "Is Phoenix-Mesa Gateway Airport an international airport?",
      a: "Phoenix-Mesa Gateway Airport (AZA) in nearby Mesa primarily serves domestic routes via Allegiant Air. For international flights, Queen Creek residents use Phoenix Sky Harbor (PHX), about 40 miles northwest. You'll need a valid passport book for any international departure from PHX.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Queen Creek", address: "21321 S Ellsworth Loop Rd, Queen Creek, AZ 85142", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Queen Creek", address: "18610 E Rittenhouse Rd, Queen Creek, AZ 85142", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Queen Creek", address: "21258 S Ellsworth Loop Rd, Ste 104, Queen Creek, AZ 85142", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
    { name: "Walgreens — San Tan Valley", address: "36305 N Gantzel Rd, San Tan Valley, AZ 85140", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Gilbert", address: "90 S Val Vista Dr, Gilbert, AZ 85296", hours: "Mon–Sun: 8AM–9PM" },
    { name: "FedEx Office — Chandler", address: "2970 E Germann Rd, Chandler, AZ 85286", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Mesa Southeast", address: "4123 E Valley Auto Dr, Mesa, AZ 85206", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Southeast Valley residents. About 20 miles from Queen Creek.", featured: true },
    { name: "AZ MVD — Casa Grande", address: "240 W Cottonwood Ln, Casa Grande, AZ 85122", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Pinal County and southern Queen Creek area residents.", featured: false },
    { name: "AZ MVD — Chandler", address: "50 S Beck Ave, Chandler, AZ 85226", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Chandler, Gilbert, and northern Queen Creek residents.", featured: false },
  ],
  airports: [
    {
      code: "AZA",
      name: "Phoenix-Mesa Gateway Airport",
      distance: "15 miles north of Queen Creek",
      address: "6033 S Sossaman Rd, Mesa, AZ 85212",
      note: "Closest airport to Queen Creek with Allegiant Air domestic routes. For international flights, you'll need to connect through larger hubs. Passport required for all international connections.",
      flightUrl: "https://www.gatewayairport.com/",
      flightLabel: "AZA Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "40 miles northwest of Queen Creek",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 45 minutes via Loop 202 and I-10.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "TUS",
      name: "Tucson International Airport",
      distance: "105 miles south of Queen Creek",
      address: "7250 S Tucson Blvd, Tucson, AZ 85756",
      note: "Southern Arizona's international hub. About 1 hour 45 minutes south via I-10. Co-located with the Western Passport Center.",
      flightUrl: "https://www.flytucson.com/",
      flightLabel: "TUS Airport",
      featured: false,
    },
  ],
};